/**
 * Google Drive URL utilities
 * Converts various Google Drive URL formats to embeddable/viewable URLs
 */

/**
 * Extract file ID from various Google Drive URL formats
 * Supports:
 * - https://drive.google.com/file/d/FILE_ID/view
 * - https://drive.google.com/open?id=FILE_ID
 * - https://docs.google.com/document/d/FILE_ID/edit
 * - https://docs.google.com/spreadsheets/d/FILE_ID/edit
 * - https://docs.google.com/presentation/d/FILE_ID/edit
 */
export const extractGoogleDriveFileId = (url) => {
  if (!url) return null;
  
  // Pattern for /d/FILE_ID/
  const dPattern = /\/d\/([a-zA-Z0-9_-]+)/;
  const dMatch = url.match(dPattern);
  if (dMatch) return dMatch[1];
  
  // Pattern for ?id=FILE_ID
  const idPattern = /[?&]id=([a-zA-Z0-9_-]+)/;
  const idMatch = url.match(idPattern);
  if (idMatch) return idMatch[1];
  
  return null;
};

/**
 * Check if URL is a Google Drive URL
 */
export const isGoogleDriveUrl = (url) => {
  if (!url) return false;
  return url.includes('drive.google.com') || url.includes('docs.google.com');
};

/**
 * Convert Google Drive URL to direct download/view URL
 */
export const getGoogleDriveDirectUrl = (url) => {
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) return url;
  return `https://drive.google.com/uc?export=view&id=${fileId}`;
};

/**
 * Convert Google Drive URL to embeddable preview URL
 */
export const getGoogleDriveEmbedUrl = (url) => {
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) return url;
  return `https://drive.google.com/file/d/${fileId}/preview`;
};

/**
 * Get thumbnail URL for Google Drive file
 */
export const getGoogleDriveThumbnail = (url, size = 220) => {
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) return null;
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${size}`;
};

/**
 * Detect content type from URL or explicit type
 */
export const detectContentType = (url, explicitType) => {
  if (explicitType) return explicitType;
  
  const lowerUrl = url?.toLowerCase() || '';
  
  if (lowerUrl.includes('.pdf') || lowerUrl.includes('document')) return 'pdf';
  if (lowerUrl.includes('.mp4') || lowerUrl.includes('.webm') || lowerUrl.includes('.mov') || lowerUrl.includes('video')) return 'video';
  if (lowerUrl.includes('.mp3') || lowerUrl.includes('.wav') || lowerUrl.includes('.ogg') || lowerUrl.includes('audio')) return 'audio';
  if (lowerUrl.includes('.png') || lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('.gif')) return 'image';
  
  return 'unknown';
};

/**
 * Validate Google Drive URL format
 */
export const validateGoogleDriveUrl = (url) => {
  if (!url) return { valid: false, error: 'URL is required' };
  
  if (!isGoogleDriveUrl(url)) {
    // Allow non-Google Drive URLs too (YouTube, Vimeo, etc.)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return { valid: true, isGoogleDrive: false };
    }
    return { valid: false, error: 'Please enter a valid URL' };
  }
  
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) {
    return { valid: false, error: 'Could not extract file ID from Google Drive URL' };
  }
  
  return { valid: true, isGoogleDrive: true, fileId };
};
